// ===== 데이터 모델 타입 정의 =====

// 취업자
export interface Worker {
  worker_id: string;
  name: string;
  pin_hash?: string;
  current_company: string;
  job_type: string;
  employment_start_date?: string;
  employment_end_date?: string;
  employment_status: EmploymentStatus;
  termination_reason?: string;
  phone_optional: string;
  status: 'active' | 'inactive';
  created_at: string;
  updated_at: string;
}

export type EmploymentStatus = '취업 중' | '휴직' | '퇴사' | '지원 종료';

export interface WorkerSignupRequest {
  request_id: string;
  name: string;
  pin_hash?: string;
  current_company: string;
  job_type: string;
  employment_start_date?: string;
  phone_optional: string;
  privacy_required_agreed?: boolean;
  sensitive_required_agreed?: boolean;
  contact_optional_agreed?: boolean;
  consent_version?: string;
  consent_at?: string;
  status: 'pending' | 'approved' | 'rejected';
  admin_memo: string;
  reviewed_by: string;
  reviewed_at: string;
  converted_worker_id: string;
  created_at: string;
  updated_at: string;
}

export interface WorkerSignupInput {
  name: string;
  pin: string;
  current_company: string;
  employment_start_date: string;
  job_type?: string;
  phone_optional?: string;
  privacy_required_agreed: boolean;
  sensitive_required_agreed: boolean;
  contact_optional_agreed?: boolean;
  consent_version: string;
}

export interface WorkerSignupReviewInput {
  request_id: string;
  status: 'approved' | 'rejected';
  admin_id: string;
  admin_memo?: string;
}

// 관리자
export interface Admin {
  admin_id: string;
  name: string;
  login_id: string;
  password_hash?: string;
  status: 'active' | 'inactive';
  must_change_password?: boolean;
  last_password_changed_at?: string;
  created_at: string;
  updated_at: string;
}

export interface ChangeAdminPasswordInput {
  admin_id: string;
  current_password: string;
  new_password: string;
}

export interface RepairWorkerFieldsResult {
  dryRun: boolean;
  count: number;
  repairedRows: {
    row: number;
    worker_id: string;
    name: string;
    field: string;
    oldValue: string;
    newValue: string;
  }[];
}

export const PRIVACY_CONSENT_VERSION = 'v1.0';

export interface PrivacyConsent {
  consent_id: string;
  user_type: 'worker' | 'signup_request';
  user_id?: string;
  request_id?: string;
  name: string;
  consent_version: string;
  privacy_required_agreed: boolean;
  sensitive_required_agreed: boolean;
  contact_optional_agreed?: boolean;
  consent_text_snapshot: string;
  agreed_at: string;
  created_at: string;
}

// 일일 체크인
export interface DailyCheckin {
  checkin_id: string;
  worker_id: string;
  checkin_date: string; // YYYY-MM-DD
  q1_mood: number;
  q2_work: number;
  q3_relationship: number;
  q4_health: number;
  q5_need_help: number;
  q6_overall: number;
  average_score: number;
  message_to_admin: string;
  message_tags: string;
  is_edited: boolean;
  original_checkin_id: string;
  created_at: string;
  updated_at: string;
}

// 체크인 입력 데이터
export interface CheckinInput {
  worker_id: string;
  checkin_date: string;
  q1_mood: number;
  q2_work: number;
  q3_relationship: number;
  q4_health: number;
  q5_need_help: number;
  q6_overall: number;
  message_to_admin?: string;
  message_tags?: string[];
}

// 수정 이력
export interface CheckinRevision {
  revision_id: string;
  checkin_id: string;
  worker_id: string;
  checkin_date: string;
  previous_payload_json: string;
  new_payload_json: string;
  edited_by: string;
  edited_at: string;
}

// 알림
export interface Alert {
  alert_id: string;
  worker_id: string;
  checkin_id: string;
  alert_date: string;
  alert_type: AlertType;
  previous_score: number;
  current_score: number;
  score_drop: number;
  reason: string;
  status: AlertStatus;
  created_at: string;
  resolved_at: string;
  resolved_by?: string;
  resolution_memo?: string;
  follow_up_required?: boolean;
  follow_up_memo?: string;
  admin_memo: string;
}

export type AlertType =
  | 'score_drop'
  | 'low_score'
  | 'consecutive_low'
  | 'consecutive_drop'
  | 'need_help'
  | 'has_message';

export type AlertStatus = 'new' | 'acknowledged' | 'monitoring' | 'resolved';

// 관리자 답변
export interface AdminReply {
  reply_id: string;
  checkin_id: string;
  worker_id: string;
  admin_id: string;
  worker_message: string;
  reply_text: string;
  message_status: MessageStatus;
  worker_read_at: string;
  created_at: string;
  updated_at: string;
}

export type MessageStatus = '미확인' | '미답변' | '확인함' | '답변완료' | '취업자읽음' | '종결';

// 공지
export interface Notice {
  notice_id: string;
  title: string;
  summary: string;
  content: string;
  start_date: string;
  end_date: string;
  is_visible: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
}

// 공지 입력
export interface NoticeInput {
  title: string;
  summary: string;
  content: string;
  start_date: string;
  end_date: string;
  is_visible: boolean;
}

// 공지 확인 기록
export interface NoticeRead {
  read_id: string;
  notice_id: string;
  user_type: 'worker' | 'admin';
  user_id: string;
  read_at: string;
}

// 상담 기록
export interface AdminNote {
  note_id: string;
  worker_id: string;
  admin_id: string;
  note_date: string;
  note_type: '상담' | '조치' | '기타';
  content: string;
  follow_up_needed: boolean;
  created_at: string;
  updated_at: string;
}

// 상담 기록 입력
export interface AdminNoteInput {
  worker_id: string;
  note_date: string;
  note_type: '상담' | '조치' | '기타';
  content: string;
  follow_up_needed: boolean;
}

export interface AttentionWorkerItem {
  worker_id: string;
  worker_name: string;
  current_company: string;
  job_type: string;
  alert_count: number;
  latest_alert_type: string;
  latest_alert_type_label?: string;
  latest_alert_message: string;
  latest_alert_date: string;
  latest_checkin_id?: string;
}

export interface DashboardMessageItem {
  reply_id: string;
  checkin_id: string;
  worker_id: string;
  worker_name: string;
  current_company: string;
  checkin_date: string;
  worker_message: string;
  message_tags: string;
  message_status: string;
  created_at: string;
}

export interface WorkerSummaryItem {
  worker_id: string;
  name: string;
  current_company: string;
  job_type: string;
  employment_start_date?: string;
  employment_status?: EmploymentStatus;
  created_at?: string;
  updated_at?: string;
}

export interface AttentionWorkerGroup {
  active: AttentionWorkerItem[];
  monitoring: AttentionWorkerItem[];
  resolved: AttentionWorkerItem[];
}

export interface ResolveAttentionInput {
  worker_id: string;
  admin_id: string;
  resolution_memo?: string;
}

export interface MarkAttentionMonitoringInput {
  worker_id: string;
  admin_id: string;
  follow_up_memo?: string;
}

export type AttendanceStatus = '출근' | '퇴근완료' | '관리자수정';

export interface AttendanceRecord {
  attendance_id: string;
  worker_id: string;
  work_date: string;
  clock_in_at: string;
  clock_out_at: string;
  attendance_status: AttendanceStatus;
  worker_memo: string;
  modified_by: string;
  modification_reason: string;
  created_at: string;
  updated_at: string;
}

export interface AttendanceUpdateInput {
  clock_in_at?: string;
  clock_out_at?: string;
  attendance_status?: AttendanceStatus;
  worker_memo?: string;
  modified_by: string;
  modification_reason: string;
}

export interface AttendanceOverviewRow {
  worker_id: string;
  name: string;
  current_company: string;
  job_type: string;
  attendance_id?: string;
  work_date: string;
  clock_in_at: string;
  clock_out_at: string;
  attendance_status: string;
}

export interface AttendanceOverviewData {
  date: string;
  totalWorkers: number;
  clockedInCount: number;
  clockedOutCount: number;
  missingInCount: number;
  missingOutCount: number;
  rows: AttendanceOverviewRow[];
}

export interface WorkerHomeData {
  worker: Worker;
  todayCheckin: DailyCheckin | null;
  activeNotices: Notice[];
  replies: AdminReply[];
  todayAttendance?: AttendanceRecord | null;
}

export interface AdminMessagesData {
  workers: Worker[];
  checkins: DailyCheckin[];
  replies: AdminReply[];
}

export interface WorkerDetailData {
  worker: Worker;
  checkins: DailyCheckin[];
  replies: AdminReply[];
  adminNotes: AdminNote[];
  alerts: Alert[];
}

// 대시보드 데이터
export interface DashboardData {
  totalWorkers: number;
  todayCheckedCount: number;
  todayUncheckedCount: number;
  todayAlertCount: number;
  unresolvedAlertCount: number;
  unansweredMessageCount: number;
  unreadNoticeCount: number;
  pendingSignupRequestCount: number;
  workersMissingEmploymentStartDate?: number;

  attentionWorkers: AttentionWorkerItem[];
  unansweredMessages: DashboardMessageItem[];
  recentAlerts: Alert[];
  uncheckedWorkers: WorkerSummaryItem[];
  recentWorkers: WorkerSummaryItem[];
  workerNames: Record<string, string>;
  todayCheckins?: DailyCheckin[];
}

// 필터
export interface AlertFilter {
  date?: string;
  month?: string;
  status?: AlertStatus;
  alert_type?: AlertType;
  worker_id?: string;
}

export interface CheckinFilter {
  worker_id?: string;
  month?: string; // YYYY-MM
  date?: string;  // YYYY-MM-DD
}

// 점수 라벨
export const SCORE_LABELS: Record<number, { emoji: string; label: string; color: string }> = {
  1: { emoji: '😢', label: '많이 힘들었어요', color: '#F44336' },
  2: { emoji: '🙁', label: '힘들었어요', color: '#FF9800' },
  3: { emoji: '😐', label: '보통이에요', color: '#FFEB3B' },
  4: { emoji: '🙂', label: '좋아요', color: '#2196F3' },
  5: { emoji: '😀', label: '아주 좋아요', color: '#4CAF50' },
};

// 질문 목록
export const CHECKIN_QUESTIONS = [
  { key: 'q1_mood', label: '오늘 기분은 어땠어요?' },
  { key: 'q2_work', label: '오늘 일은 어땠어요?' },
  { key: 'q3_relationship', label: '직장에서 사람들과 잘 지냈어요?' },
  { key: 'q4_health', label: '몸은 괜찮았어요?' },
  { key: 'q5_need_help', label: '도움이 필요하다고 느꼈나요?' },
  { key: 'q6_overall', label: '오늘 하루는 전체적으로 어땠어요?' },
] as const;

// 메시지 태그
export const MESSAGE_TAGS = [
  '오늘 힘들었어요',
  '이야기하고 싶어요',
  '직장에서 속상한 일이 있었어요',
  '칭찬받았어요',
  '몸이 아팠어요',
  '일이 어려웠어요',
  '사람들과 지내기 어려웠어요',
  '괜찮아요',
] as const;

// 인증 사용자 타입
export interface AuthUser {
  type: 'worker' | 'admin';
  id: string;
  name: string;
  mustChangePassword?: boolean;
}

export interface MonthlyReportData {
  year: string;
  month: string;

  currentWorkerCount: number;
  eligibleWorkerDays: number;
  workersMissingEmploymentStartDate: number;
  uniqueWorkers: number;
  totalCheckins: number;
  completionRate: number;
  averageScore: number;

  alertCount: number;
  unansweredMessageCount: number;
  workerMessageCount: number;
  adminReplyCount: number;
  workerReadReplyCount: number;
  closedMessageCount: number;
  adminNoteCount: number;

  dailyStats: {
    date: string;
    eligibleWorkerCount: number;
    completedCount: number;
    missingCount: number;
    averageScore: number;
    alertCount: number;
    messageCount: number;
    unansweredCount: number;
  }[];

  questionAverages: {
    key: string;
    label: string;
    averageScore: number;
    previousMonthDiff?: number;
  }[];

  alertBreakdown: {
    type: string;
    label: string;
    count: number;
  }[];

  observations: string[];
  supportNeeds: string[];

  generatedAt: string;
}
